if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.loadingSpinner = this.querySelector('.loading__spinner');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (this.loadingSpinner) this.loadingSpinner.classList.remove('hidden');

        const isCartDrawer = this.cart && this.cart.tagName.toLowerCase() === 'cart-drawer';
        if (isCartDrawer && typeof this.cart.prepareForAdd === 'function') {
          this.cart.prepareForAdd(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        const hasIncludedStorageBag = this.dataset.includedStorageBag === 'true';
        const selectedVariantId = parseInt(formData.get('id'), 10);
        const selectedQuantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);
        const bundleItems = this.getBundleComponentItems(selectedVariantId, selectedQuantity);
        const bundleProperties = this.getBundleParentProperties(bundleItems);

        if (bundleItems.length && selectedVariantId) {
          const parentItem = { id: selectedVariantId, quantity: selectedQuantity };

          if (bundleProperties || hasIncludedStorageBag) {
            parentItem.properties = {
              ...(bundleProperties || {}),
              ...(hasIncludedStorageBag
                ? { Includes: 'Complimentary cotton storage bag — valued at $19' }
                : {}),
            };
          }

          const items = [parentItem];

          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify({
            items,
            ...(this.cart
              ? {
                  sections: this.cart.getSectionsToRender().map((section) => section.id),
                  sections_url: window.location.pathname,
                }
              : {}),
          });
        } else {
          config.body = formData;
        }

        const cartAddUrl =
          bundleItems.length && selectedVariantId && `${routes.cart_add_url}`.indexOf('.js') === -1
            ? `${routes.cart_add_url}.js`
            : `${routes.cart_add_url}`;

        fetch(cartAddUrl, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              if (isCartDrawer && typeof this.cart.markAddFailed === 'function') {
                this.cart.markAddFailed(response.description || response.message);
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      if (typeof this.cart.openAfterAdd === 'function') {
                        this.cart.openAfterAdd(response);
                      } else {
                        this.cart.renderContents(response);
                      }
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                if (typeof this.cart.openAfterAdd === 'function') {
                  this.cart.openAfterAdd(response);
                } else {
                  this.cart.renderContents(response);
                }
              });
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          })
          .catch((e) => {
            console.error(e);
            if (isCartDrawer && typeof this.cart.markAddFailed === 'function') {
              this.cart.markAddFailed('Unable to add this item. Please try again.');
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (this.loadingSpinner) this.loadingSpinner.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
        });
      }

      getBundleComponentItems(selectedVariantId, selectedQuantity) {
        const bundleRoot = this.querySelector('[data-wo-bundle-components]');
        if (!bundleRoot || !selectedVariantId) return [];

        const selectedOption = this.querySelector(`[data-wo-variant-select] option[value="${selectedVariantId}"]`);
        const selectedParts = selectedOption
          ? (selectedOption.dataset.opts || '').split('|').filter((part) => part && part !== 'Default Title')
          : [];
        const bundleTitle = bundleRoot.dataset.bundleTitle || 'Walk Kit';
        const bundleHandle = bundleRoot.dataset.bundleHandle || '';

        return Array.from(bundleRoot.querySelectorAll('[data-wo-bundle-component]'))
          .map((select) => {
            const options = Array.from(select.options).filter((option) => option.dataset.available === 'true');
            const candidates = options.length ? options : Array.from(select.options);
            if (!candidates.length) return null;

            const match =
              candidates
                .map((option) => {
                  const parts = (option.dataset.options || '').split('|').filter((part) => part && part !== 'Default Title');
                  const score = parts.reduce((total, part) => total + (selectedParts.includes(part) ? 1 : 0), 0);
                  return { option, score };
                })
                .sort((a, b) => b.score - a.score)[0].option || candidates[0];

            return {
              id: parseInt(match.value, 10),
              quantity: selectedQuantity,
              properties: {
                kitComponentRole: select.dataset.componentRole || select.dataset.productTitle || '',
                _bundle_parent: bundleTitle,
                _bundle_parent_handle: bundleHandle,
                _bundle_component: select.dataset.productTitle || '',
              },
            };
          })
          .filter(Boolean);
      }

      getBundleParentProperties(bundleItems) {
        if (!bundleItems || !bundleItems.length) return null;

        const firstComponent = bundleItems[0];
        const bundleTitle = firstComponent.properties && firstComponent.properties._bundle_parent;
        const bundleHandle = firstComponent.properties && firstComponent.properties._bundle_parent_handle;
        const componentRoles = bundleItems
          .map((item) => item.properties && item.properties.kitComponentRole)
          .filter(Boolean);
        const componentTitles = bundleItems
          .map((item) => item.properties && item.properties._bundle_component)
          .filter(Boolean);
        const componentVariantIds = bundleItems
          .map((item) => item.id)
          .filter(Boolean);

        return {
          'Kit includes': 'Harness, Lead, Collar',
          _bundle_parent: bundleTitle || 'Walk Kit',
          _bundle_parent_handle: bundleHandle || '',
          _bundle_component_products: componentTitles.join(' | '),
          _bundle_component_variant_ids: componentVariantIds.join(','),
        };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
